import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { RefreshCw, Target, Briefcase, MapPin, DollarSign, Sparkles, Info } from "lucide-react";

interface CandidateJobScoreProps {
  candidateId: string;
  jobId: string;
  showRefresh?: boolean;
  compact?: boolean;
}

interface ScoreData {
  id: string;
  overall_score: number;
  skill_score: number;
  experience_score: number;
  location_score: number;
  salary_score: number;
  ai_reasoning: string | null;
  score_breakdown: {
    skillsMatched?: string[];
    skillsMissing?: string[];
    experienceHighlights?: string;
    locationNotes?: string;
    salaryNotes?: string;
  };
  scored_at: string;
  is_auto_scored: boolean;
}

interface ScoringProfile {
  id: string;
  name: string;
  is_default: boolean;
}

export default function CandidateJobScore({ 
  candidateId, 
  jobId, 
  showRefresh = true,
  compact = false 
}: CandidateJobScoreProps) {
  const queryClient = useQueryClient();
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  const { data: score, isLoading: scoreLoading } = useQuery({
    queryKey: ["candidate-job-score", candidateId, jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("candidate_job_scores")
        .select("*")
        .eq("candidate_id", candidateId)
        .eq("job_id", jobId)
        .maybeSingle();
      if (error) throw error;
      return data as ScoreData | null;
    },
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ["scoring-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scoring_weight_profiles")
        .select("id, name, is_default")
        .order("is_system", { ascending: false });
      if (error) throw error;
      return data as ScoringProfile[];
    },
  });

  const scoreMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("score-candidate", {
        body: { 
          candidateId, 
          jobId,
          profileId: selectedProfileId,
          isManual: true 
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidate-job-score", candidateId, jobId] });
      toast.success("Score generated successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to generate score");
    },
  });

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    if (score >= 40) return "text-orange-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return "bg-green-100";
    if (score >= 60) return "bg-yellow-100";
    if (score >= 40) return "bg-orange-100";
    return "bg-red-100";
  };

  const getProgressColor = (score: number) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    if (score >= 40) return "bg-orange-500";
    return "bg-red-500";
  };

  if (scoreLoading) {
    return compact ? (
      <Skeleton className="h-8 w-16" />
    ) : (
      <Card>
        <CardContent className="p-4">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Compact view - just the score badge
  if (compact) {
    if (!score) {
      return (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => scoreMutation.mutate()}
          disabled={scoreMutation.isPending}
        >
          {scoreMutation.isPending ? (
            <RefreshCw className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          <span className="ml-1">Score</span>
        </Button>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className={`${getScoreBgColor(score.overall_score)} ${getScoreColor(score.overall_score)} border-0 cursor-help`}>
              {Math.round(score.overall_score)}% Match
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-1 text-xs">
              <p><strong>Skills:</strong> {score.skill_score}%</p>
              <p><strong>Experience:</strong> {score.experience_score}%</p>
              <p><strong>Location:</strong> {score.location_score}%</p>
              <p><strong>Salary:</strong> {score.salary_score}%</p>
              {score.ai_reasoning && (
                <p className="pt-1 border-t mt-1">{score.ai_reasoning}</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Full view
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Match Score
          </CardTitle>
          {showRefresh && (
            <div className="flex items-center gap-2">
              <Select 
                value={selectedProfileId || ""} 
                onValueChange={(v) => setSelectedProfileId(v || null)}
              >
                <SelectTrigger className="w-[160px] h-8">
                  <SelectValue placeholder="Default profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Default profile</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.is_default && "(Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => scoreMutation.mutate()}
                disabled={scoreMutation.isPending}
              >
                {scoreMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                {score ? "Re-score" : "Generate"}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!score ? (
          <div className="text-center py-4 text-muted-foreground">
            <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No score available yet</p>
            <p className="text-sm">Click Generate to create an AI match score</p>
          </div>
        ) : (
          <>
            {/* Overall Score */}
            <div className="flex items-center justify-center py-2">
              <div className={`text-5xl font-bold ${getScoreColor(score.overall_score)}`}>
                {Math.round(score.overall_score)}%
              </div>
            </div>

            {/* Individual Scores */}
            <div className="grid grid-cols-2 gap-3">
              <ScoreItem 
                icon={Target} 
                label="Skills" 
                score={score.skill_score} 
                getProgressColor={getProgressColor}
              />
              <ScoreItem 
                icon={Briefcase} 
                label="Experience" 
                score={score.experience_score} 
                getProgressColor={getProgressColor}
              />
              <ScoreItem 
                icon={MapPin} 
                label="Location" 
                score={score.location_score} 
                getProgressColor={getProgressColor}
              />
              <ScoreItem 
                icon={DollarSign} 
                label="Salary" 
                score={score.salary_score} 
                getProgressColor={getProgressColor}
              />
            </div>

            {/* AI Reasoning */}
            {score.ai_reasoning && (
              <div className="pt-3 border-t">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">{score.ai_reasoning}</p>
                </div>
              </div>
            )}

            {/* Score Breakdown */}
            {score.score_breakdown && (
              <div className="pt-3 border-t space-y-2">
                {score.score_breakdown.skillsMatched?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs text-muted-foreground">Matched skills:</span>
                    {score.score_breakdown.skillsMatched.map((skill, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>
                    ))}
                  </div>
                )}
                {score.score_breakdown.skillsMissing?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs text-muted-foreground">Missing skills:</span>
                    {score.score_breakdown.skillsMissing.map((skill, i) => (
                      <Badge key={i} variant="outline" className="text-xs text-destructive">{skill}</Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Metadata */}
            <div className="text-xs text-muted-foreground pt-2 border-t flex justify-between">
              <span>
                Scored {new Date(score.scored_at).toLocaleDateString()}
                {score.is_auto_scored && " (auto)"}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ScoreItem({ 
  icon: Icon, 
  label, 
  score, 
  getProgressColor 
}: { 
  icon: any; 
  label: string; 
  score: number; 
  getProgressColor: (s: number) => string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{label}</span>
        </div>
        <span className="font-medium">{Math.round(score)}%</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div 
          className={`h-full ${getProgressColor(score)} transition-all`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}
